import { inject, Injectable } from '@angular/core';
import { EMPTY, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { Option } from '@sapphire/result';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { selectCanManageMultiBuyerUsers } from '@ps/auth-data-access';
import { selectAllBuyerSettings } from '@ps/user-settings-data-access';

import { UserService } from '../services/user.service';
import { toManagedUserDraft } from '../utils/managed-user-draft.helper';
import { UserActions } from './user.actions';
import {
  selectPage,
  selectSearch,
  selectUserViewModel,
} from './user.selectors';
import { FIRST_PAGE, USERS_PAGE_SIZE } from './user-state.constants';

const COMPANY_REQUIRED_ERROR = 'Select a company before saving the user.';

@Injectable()
export class UserEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _service = inject(UserService);
  private readonly _toast = inject(ToastService);

  public loadUsers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadUsers),
      concatLatestFrom(() => this._store.select(selectSearch)),
      switchMap(([{ page }, search]) =>
        this._service.users(page, USERS_PAGE_SIZE, search).pipe(
          map(({ users, count }) =>
            UserActions.loadUsersSuccess({ users, count }),
          ),
          this.psCatchError((error) =>
            of(UserActions.loadUsersFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /** A new search always restarts at the first page. */
  public reloadOnSearch$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.changeSearch),
      map(() => UserActions.loadUsers({ page: FIRST_PAGE })),
    ),
  );

  public loadCompanies$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadCompanies),
      concatLatestFrom(() =>
        this._store.select(selectCanManageMultiBuyerUsers),
      ),
      switchMap(([, canManageMultiBuyerUsers]) =>
        this._service.companies(canManageMultiBuyerUsers).pipe(
          map((companies) => UserActions.loadCompaniesSuccess({ companies })),
          this.psCatchError((error) =>
            of(UserActions.loadCompaniesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /**
   * Selecting a user always refetches its company, so the loading overlay —
   * driven by the HTTP interceptor — shows on every pick. It lives here rather
   * than in the form: dispatching from a component `effect()` re-enters change
   * detection mid-flush, which throws inside the signal-forms bindings and
   * kills every effect queued behind it. PRIV-1102.
   */
  public loadDetailOnSelect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.selectUser),
      concatLatestFrom(() => this._store.select(selectUserViewModel)),
      mergeMap(([, { selectedUser }]) =>
        Option.from(selectedUser?.companyId).match({
          some: (companyId) => of(UserActions.loadCompanyDetail({ companyId })),
          none: () => EMPTY,
        }),
      ),
    ),
  );

  public loadCompanyDetail$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadCompanyDetail),
      switchMap(({ companyId }) =>
        this._service.companyDetail(companyId).pipe(
          map((companyDetail) =>
            UserActions.loadCompanyDetailSuccess({ companyDetail }),
          ),
          this.psCatchError((error) =>
            of(UserActions.loadCompanyDetailFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /**
   * What a company needs loaded next is decided from the company itself, so it
   * belongs here rather than in the form: an effect watching the view model
   * would re-fire on every slice change and loop.
   */
  public loadCompanyDependants$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadCompanyDetailSuccess),
      concatLatestFrom(() => this._store.select(selectAllBuyerSettings)),
      mergeMap(([{ companyDetail }, buyerSettings]) => {
        const next: Action[] = [];

        if (companyDetail.multiBuyerEntityIds.length) {
          next.push(
            UserActions.loadMultiBuyerEntities({ companyId: companyDetail.id }),
          );
        }

        if (buyerSettings.enableViewByPM?.includes(companyDetail.id)) {
          next.push(
            UserActions.loadProjectManagers({ companyId: companyDetail.id }),
          );
        }

        return next;
      }),
    ),
  );

  public loadMultiBuyerEntities$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadMultiBuyerEntities),
      switchMap(({ companyId }) =>
        this._service.multiBuyerEntities(companyId).pipe(
          map((entities) =>
            UserActions.loadMultiBuyerEntitiesSuccess({ entities }),
          ),
          this.psCatchError((error) =>
            of(
              UserActions.loadMultiBuyerEntitiesFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public loadProjectManagers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadProjectManagers),
      switchMap(({ companyId }) =>
        this._service.projectManagers(companyId).pipe(
          map((projectManagers) =>
            UserActions.loadProjectManagersSuccess({ projectManagers }),
          ),
          this.psCatchError((error) =>
            of(
              UserActions.loadProjectManagersFailure({ error: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  public saveUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.saveUser),
      concatLatestFrom(() => this._store.select(selectUserViewModel)),
      switchMap(([{ form }, viewModel]) =>
        toManagedUserDraft(form, viewModel).match({
          some: (draft) =>
            this._service.saveUser(draft).pipe(
              // The backend writes the wording the operator sees.
              tap((result) => this._toast.success(result.message)),
              map((result) => UserActions.saveUserSuccess({ result })),
              this.psCatchError((error) =>
                of(UserActions.saveUserFailure({ error: error.message })),
              ),
            ),
          none: () =>
            of(UserActions.saveUserFailure({ error: COMPANY_REQUIRED_ERROR })),
        }),
      ),
    ),
  );

  public toggleUserStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.toggleUserStatus),
      switchMap(({ user }) =>
        this._service.toggleStatus(user).pipe(
          tap((message) => this._toast.success(message)),
          map((message) => UserActions.toggleUserStatusSuccess({ message })),
          this.psCatchError((error) =>
            of(UserActions.toggleUserStatusFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  public reloadAfterWrite$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.saveUserSuccess, UserActions.toggleUserStatusSuccess),
      concatLatestFrom(() => this._store.select(selectPage)),
      map(([, page]) => UserActions.loadUsers({ page })),
    ),
  );

  public loadApiToken$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.loadApiToken),
      switchMap(({ userId }) =>
        this._service.apiToken(userId).pipe(
          map((token) => UserActions.loadApiTokenSuccess({ token })),
          this.psCatchError((error) =>
            of(UserActions.loadApiTokenFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );
}
